import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  RouterProvider,
  useLocation,
} from "@tanstack/react-router";
import { lazy, type ReactNode, Suspense } from "react";
import { adminRoute } from "./admin/routes.js";
import { Notice } from "./app-frame.js";
import { SignedInLayout } from "./app.js";
import { AppLink } from "./components/app-link.js";
import { CrashPage } from "./components/error-boundary.js";
import { PageState } from "./components/page-state.js";
import { useMay } from "./lib/queries/index.js";
import { PATHS, type ScreenKind } from "./lib/routes.js";
import { useSignedIn } from "./lib/signed-in.js";
import { AboutScreen } from "./screens/about-screen.js";
import { CallbackScreen } from "./screens/callback-screen.js";
import { NotFoundScreen } from "./screens/not-found-screen.js";
import { consentRoute } from "./screens/oauth/route.js";
import { ResetPasswordScreen } from "./screens/reset-password-screen.js";
import { SignInRoute } from "./screens/sign-in-route.js";

/**
 * Every address, and what it draws.
 *
 * The addresses are exactly the ones the hand-rolled switch answered, so every
 * link anyone saved and every tab left open still lands where it did
 * (AGENTS.md §0f). Report screens keep the report in `?report=`; filters stay
 * in the querystring, read by `lib/url-filters.ts`.
 *
 * What the router adds over the switch it replaced: a new page opens at the
 * top and Back returns to where you were; a crash is caught per screen, inside
 * the rail; and each screen's code is fetched when it is first opened rather
 * than all of it up front.
 *
 * The screens stay plain components that take their workspace as props. Each
 * route below is the few lines that read the signed-in context and hand it
 * over, so a screen never picks its own tenant and a test can render one alone.
 */

/*
 * Each screen in its own chunk. `lazy` wants a default export and this
 * codebase has none, so the named one is lifted into place at the seam.
 */
const OverviewScreen = lazy(() =>
  import("./screens/overview-screen.js").then((m) => ({ default: m.OverviewScreen })),
);
const PromptsScreen = lazy(() =>
  import("./screens/prompts-screen.js").then((m) => ({ default: m.PromptsScreen })),
);
const RivalsScreen = lazy(() =>
  import("./screens/rivals/rivals-screen.js").then((m) => ({ default: m.RivalsScreen })),
);
const OpportunitiesScreen = lazy(() =>
  import("./screens/opportunities/opportunities-screen.js").then((m) => ({
    default: m.OpportunitiesScreen,
  })),
);
const CitationsScreen = lazy(() =>
  import("./screens/citations-screen.js").then((m) => ({ default: m.CitationsScreen })),
);
const PublishScreen = lazy(() =>
  import("./screens/publish/publish-screen.js").then((m) => ({
    default: m.PublishScreen,
  })),
);
const BrandSettingsScreen = lazy(() =>
  import("./screens/brand-settings-screen.js").then((m) => ({
    default: m.BrandSettingsScreen,
  })),
);
const ResearchScreen = lazy(() =>
  import("./screens/research-screen.js").then((m) => ({ default: m.ResearchScreen })),
);
const LibraryScreen = lazy(() =>
  import("./screens/library-screen.js").then((m) => ({ default: m.LibraryScreen })),
);
const AddPromptsScreen = lazy(() =>
  import("./screens/add-prompts-screen.js").then((m) => ({
    default: m.AddPromptsScreen,
  })),
);
const FanoutScreen = lazy(() =>
  import("./screens/fanout-screen.js").then((m) => ({ default: m.FanoutScreen })),
);
const AiAppsScreen = lazy(() =>
  import("./screens/ai-apps/ai-apps-screen.js").then((m) => ({
    default: m.AiAppsScreen,
  })),
);
const PeopleScreen = lazy(() =>
  import("./screens/people/people-screen.js").then((m) => ({ default: m.PeopleScreen })),
);
const InviteScreen = lazy(() =>
  import("./screens/invite-screen.js").then((m) => ({ default: m.InviteScreen })),
);
const ProfileScreen = lazy(() =>
  import("./screens/profile-screen.js").then((m) => ({ default: m.ProfileScreen })),
);
const FanoutDetailScreen = lazy(() =>
  import("./screens/fanout-detail-screen.js").then((m) => ({
    default: m.FanoutDetailScreen,
  })),
);

const rootRoute = createRootRoute({ component: Outlet });

const aboutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/about",
  component: AboutScreen,
});

/*
 * Public, like the callback: `/` is the public site for a visitor, so signing
 * in has its own address (lib/sign-in-path.ts).
 */
const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-in",
  component: SignInRoute,
});

const callbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/callback",
  component: CallbackScreen,
});

/* Public like the callback: whoever opens it cannot sign in yet. */
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  component: ResetPasswordScreen,
});

/** No path of its own: it wraps every signed-in address in the session and rail. */
/*
 * An invite, opened by someone who may not have an account yet. Public for the
 * same reason as the callback: its reader is not signed in to anything here.
 */
const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invite/$token",
  component: Invite,
});

function Invite() {
  const { token } = inviteRoute.useParams();
  return (
    <Suspense fallback={null}>
      <InviteScreen token={token} />
    </Suspense>
  );
}

const signedInRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "signed-in",
  component: SignedInLayout,
});

/** A route under the signed-in layout, and which screen it draws. */
function screen<const TPath extends string>(
  path: TPath,
  kind: ScreenKind,
  component: () => ReactNode,
) {
  return createRoute({
    getParentRoute: () => signedInRoute,
    path,
    staticData: { screen: kind },
    component,
  });
}

function Overview() {
  const { workspaceId, reportId } = useSignedIn();
  if (reportId === null) {
    return (
      <Notice>
        This workspace has no brand report yet, so there is nothing to summarise. Add a
        brand from the rail and the Overview fills in.
      </Notice>
    );
  }
  return (
    <OverviewScreen workspaceId={workspaceId} reportId={reportId} today={new Date()} />
  );
}

function Prompts() {
  const { workspaceId, brands, reportId } = useSignedIn();
  return (
    <PromptsScreen
      workspaceId={workspaceId}
      brands={brands}
      reportId={reportId}
      today={new Date()}
    />
  );
}

/*
 * Report-scoped screens that take a missing report themselves. They say so on
 * the page, which keeps the filters on screen and never shows a person who
 * followed a link a bare sentence where a page was.
 */
function Citations() {
  const { workspaceId, brands, reportId } = useSignedIn();
  return (
    <CitationsScreen
      workspaceId={workspaceId}
      reportId={reportId}
      brands={brands}
      today={new Date()}
    />
  );
}

/** Like Citations: no early return for a missing report; the screen says so itself. */
function onReport(Screen: typeof RivalsScreen) {
  return function OnReport() {
    const { workspaceId, reportId } = useSignedIn();
    return <Screen workspaceId={workspaceId} reportId={reportId} today={new Date()} />;
  };
}

const Rivals = onReport(RivalsScreen);
const Opportunities = onReport(OpportunitiesScreen);
const OffPage = onReport(PublishScreen);

function BrandSettings() {
  const { workspaceId, reportId } = useSignedIn();
  return <BrandSettingsScreen workspaceId={workspaceId} reportId={reportId} />;
}

/*
 * Workspace-wide: they need no report. Research and fan-out run before there
 * is a report to run them for; the library only wants the brands to put a face
 * on the reports reading each question.
 */
/*
 * Writing questions needs the Questions switch; reading a page search does
 * not. The screen holds back what may not be used, tool by tool.
 */
function Research() {
  return <ResearchScreen workspaceId={useSignedIn().workspaceId} />;
}

function Library() {
  const { workspaceId, brands } = useSignedIn();
  return <LibraryScreen workspaceId={workspaceId} brands={brands} />;
}

/** Adding questions needs the Questions switch; without it the page says so. */
function AddPrompts() {
  const { workspaceId } = useSignedIn();
  const may = useMay(workspaceId, "questions");
  if (may === "unknown") return null;
  if (may === "no") {
    return (
      <PageState
        title="Adding questions is switched off for you"
        actions={
          <AppLink href={PATHS.library} className="button-primary">
            Back to the library
          </AppLink>
        }
      >
        An owner or admin decides who adds questions. Ask one of them to switch on Add and
        remove questions for you.
      </PageState>
    );
  }
  return <AddPromptsScreen workspaceId={workspaceId} />;
}

function Fanout() {
  return <FanoutScreen workspaceId={useSignedIn().workspaceId} />;
}

function FanoutStudy() {
  const { studyId } = fanoutStudyRoute.useParams();
  return <FanoutDetailScreen workspaceId={useSignedIn().workspaceId} studyId={studyId} />;
}

/** Yours, not the workspace's: it needs nothing from the signed-in context. */
function Profile() {
  return <ProfileScreen />;
}

function Members() {
  return <PeopleScreen />;
}

function AiApps() {
  return <AiAppsScreen />;
}

/** Inside the rail, so a wrong address still leaves the way out on screen. */
function NotFound() {
  return <NotFoundScreen path={useLocation().pathname} />;
}

/** Setup takes this address one level up, in the layout; the route only claims it. */
function NewBrand() {
  return null;
}

/*
 * Prompts for a page became Find questions' page tool (2026-10-12). Its old
 * address still works: it lands on the tool, with the search it named.
 */
const pagePromptsMoved = createRoute({
  getParentRoute: () => signedInRoute,
  path: PATHS.urlPrompts,
  beforeLoad: ({ location }) => {
    const params = new URLSearchParams(location.searchStr);
    params.set("m", "page");
    redirect({
      href: `${PATHS.research}?${params.toString()}`,
      replace: true,
      throw: true,
    });
  },
});

const fanoutStudyRoute = screen(`${PATHS.fanout}/$studyId`, "fanout-study", FanoutStudy);

const routeTree = rootRoute.addChildren([
  // The operator panel: present only in a build that switched it on (admin/routes.tsx).
  adminRoute(rootRoute),
  aboutRoute,
  signInRoute,
  callbackRoute,
  resetPasswordRoute,
  inviteRoute,
  consentRoute(rootRoute),
  signedInRoute.addChildren([
    screen(PATHS.home, "overview", Overview),
    screen(PATHS.overview, "overview", Overview),
    screen(PATHS.prompts, "prompts", Prompts),
    screen(PATHS.rivals, "rivals", Rivals),
    screen(PATHS.opportunities, "opportunities", Opportunities),
    screen(PATHS.citations, "citations", Citations),
    screen(PATHS.offPage, "offPage", OffPage),
    screen(PATHS.settings, "settings", BrandSettings),
    screen(PATHS.research, "research", Research),
    screen(PATHS.library, "library", Library),
    screen(PATHS.addPrompts, "addPrompts", AddPrompts),
    screen(PATHS.fanout, "fanout", Fanout),
    fanoutStudyRoute,
    pagePromptsMoved,
    screen(PATHS.newBrand, "newBrand", NewBrand),
    screen(PATHS.profile, "profile", Profile),
    screen(PATHS.members, "members", Members),
    screen(PATHS.aiApps, "aiApps", AiApps),
    screen("$", "not-found", NotFound),
  ]),
]);

export const router = createRouter({
  routeTree,
  // New page, top of the page; Back, back where you were.
  scrollRestoration: true,
  // Each screen gets its own boundary inside the rail.
  defaultErrorComponent: CrashPage,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export function App() {
  return <RouterProvider router={router} />;
}
